import React, { useEffect, useState } from "react";
import { FiCheckCircle } from "react-icons/fi";
import SectionHeading from "../components/SectionHeading";
import StarRating from "../components/StarRating";
import api from "../services/api";

const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

/**
 * What customers say, and the form for saying it.
 *
 * Nothing written here goes on the site by itself — every review waits until
 * it has been read and approved, which is what makes the ones shown worth
 * reading. The page says so plainly rather than implying it appears at once.
 */
export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [average, setAverage] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.listReviews().then((res) => {
      if (!mounted) return;
      if (res.ok) {
        setReviews(res.reviews || []);
        setAverage(res.averageRating);
        setTotal(res.total || 0);
      }
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const canSubmit =
    !sending && name.trim().length >= 2 && rating >= 1 && body.trim().length >= 10;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    setError(null);
    const res = await api.submitReview({ name, role, email, rating, title, body });
    setSending(false);
    if (res.ok) setSent(true);
    else setError(res.message || "Your review could not be sent. Please try again.");
  }

  return (
    <>
      <section className="section-container py-16 lg:py-20">
        <SectionHeading
          overline="Reviews"
          title="What our customers say"
          subtitle={
            total > 0
              ? `${total} review${total === 1 ? "" : "s"} from people who use Money Flow Accounting.`
              : "Real reviews from people who use Money Flow Accounting."
          }
        />

        {average !== null && (
          <div className="mt-8 flex flex-col items-center gap-2">
            <StarRating value={Math.round(average)} size={26} />
            <p className="text-sm text-slate-600">
              <strong className="text-navy">{average.toFixed(1)}</strong> out of 5
            </p>
          </div>
        )}

        <div className="mt-12">
          {loading ? (
            <p className="text-center text-sm text-slate-500">Loading reviews…</p>
          ) : reviews.length === 0 ? (
            <div className="card mx-auto max-w-xl p-8 text-center">
              <p className="text-slate-600">
                No reviews yet. If you use Money Flow Accounting, yours would be the first.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {reviews.map((review) => (
                <article key={review.id} className="card flex flex-col p-6">
                  <StarRating value={review.rating} />
                  {review.title && (
                    <h3 className="mt-3 font-semibold text-navy">{review.title}</h3>
                  )}
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                    {review.body}
                  </p>
                  <footer className="mt-4 border-t border-line pt-4">
                    <p className="text-sm font-semibold text-navy">{review.name}</p>
                    {review.role && (
                      <p className="text-xs text-slate-500">{review.role}</p>
                    )}
                    <p className="mt-1 text-xs text-slate-400">
                      {fmtDate(review.approvedAt || review.submittedAt)}
                    </p>
                  </footer>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-line bg-surface">
        <div className="section-container py-16 lg:py-20">
          <SectionHeading
            overline="Write a review"
            title="Tell us how it is going"
            subtitle="Your review is read before it goes on the site, so it may take a day or two to appear."
          />

          <div className="card mx-auto mt-10 max-w-2xl p-8">
            {sent ? (
              <div className="text-center">
                <FiCheckCircle className="mx-auto text-emerald-500" size={44} />
                <h3 className="mt-4 text-xl font-bold text-navy">Thank you</h3>
                <p className="mt-2 text-sm text-slate-600">
                  Your review has been sent. It will appear on this page once it has been
                  read.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && (
                  <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <label className="mb-1.5 block text-sm font-medium text-navy">
                  Your rating <span className="text-red-500">*</span>
                </label>
                <StarRating
                  value={rating}
                  onChange={setRating}
                  size={30}
                  label="Your rating"
                />

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="rv-name">
                      Your name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="rv-name"
                      className="input-field"
                      placeholder="Jane Carter"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="rv-role">
                      Business or job title
                    </label>
                    <input
                      id="rv-role"
                      className="input-field"
                      placeholder="Owner, Carter Landscaping"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="rv-email">
                    Email
                  </label>
                  <input
                    id="rv-email"
                    type="email"
                    className="input-field"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Only so we can reach you about your review — it is never shown on the
                    site.
                  </p>
                </div>

                <div className="mt-4">
                  <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="rv-title">
                    Headline
                  </label>
                  <input
                    id="rv-title"
                    className="input-field"
                    placeholder="Reconciling takes minutes now"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div className="mt-4">
                  <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="rv-body">
                    Your review <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="rv-body"
                    rows={5}
                    className="input-field"
                    placeholder="What has it been like to use?"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? "Sending..." : "Submit Review"}
                </button>
                {!canSubmit && !sending && (
                  <p className="mt-2 text-center text-sm text-slate-500">
                    Choose a rating, add your name, and write at least a sentence.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
